import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconLeft, IconSearch } from '../components/icons';
import MobileComposerAttachments, { type MobileActionAttachment } from '../components/MobileComposerAttachments';
import { mobileMessage } from '../components/mobileFeedback';

type Action = { code: string; label: string; requiresOpinion?: boolean };
export type { MobileActionAttachment } from '../components/MobileComposerAttachments';

type Props = {
  action: Action;
  onClose: () => boolean;
  onSubmit: () => Promise<void>;
  opinion: string;
  onOpinionChange: (value: string) => void;
  targetUserId?: string;
  onTargetChange: (value?: string) => void;
  candidates: Array<{ userId: string; name: string; organizationName?: string }>;
  onSearchTargets: (keyword: string) => void;
  busy: boolean;
  editingDisabled: boolean;
  uploading: boolean;
  attachments: readonly MobileActionAttachment[];
  onAddFiles: (files: File[]) => Promise<void>;
  onRemoveAttachment: (id: string) => void;
};

function MobileTaskActionPage(props: Props) {
  const { action, onClose, onSubmit, opinion, candidates } = props;
  const selectedUsers = props.targetUserId ? [props.targetUserId] : [];
  const [draftUsers, setDraftUsers] = useState<string[]>([]);
  const [userSheetOpen, setUserSheetOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [closing, setClosing] = useState(false);
  const interactionRef = useRef<'closing' | 'submitting' | 'uploading' | undefined>(undefined);
  const editingLocked = closing || props.busy || props.editingDisabled || props.uploading;
  const visibleCandidates = candidates;

  const close = () => {
    if (interactionRef.current || props.busy || props.editingDisabled || props.uploading) return;
    if (userSheetOpen) { setUserSheetOpen(false); return; }
    interactionRef.current = 'closing';
    setClosing(true);
  };

  useLayoutEffect(() => {
    if (closing && (props.busy || props.editingDisabled || props.uploading)) {
      interactionRef.current = undefined;
      setClosing(false);
    }
  }, [closing, props.busy, props.editingDisabled, props.uploading]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      close();
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [userSheetOpen, props.busy, props.editingDisabled, props.uploading]);

  const submit = () => {
    if (interactionRef.current || props.busy || props.uploading || userSheetOpen) return;
    if (action.requiresOpinion && !opinion.trim()) { mobileMessage.warning('请输入审批意见'); return; }
    if (action.code === 'TRANSFER' && !selectedUsers.length) { mobileMessage.warning('请添加转交人'); return; }
    interactionRef.current = 'submitting';
    const release = () => { if (interactionRef.current === 'submitting') interactionRef.current = undefined; };
    void onSubmit().then(release, release);
  };

  const addFiles = async (files: File[]) => {
    if (interactionRef.current || props.busy || props.editingDisabled || props.uploading) return;
    interactionRef.current = 'uploading';
    try { await props.onAddFiles(files); }
    finally { if (interactionRef.current === 'uploading') interactionRef.current = undefined; }
  };

  return <div className={`mobile-action-layer${closing ? ' is-closing' : ''}`} role="dialog" aria-modal="true" aria-label={action.label}>
    <button className="mobile-action-layer__mask" type="button" aria-label="关闭审批意见" onClick={close} />
    <div className="mobile-action-page" onAnimationEnd={event => {
      if (event.target !== event.currentTarget || event.animationName !== 'mobile-action-slide-out' || interactionRef.current !== 'closing') return;
      if (props.busy || props.editingDisabled || props.uploading || !onClose()) {
        interactionRef.current = undefined; setClosing(false);
      }
    }}>
    <header className="mobile-action-page__header">
      <button type="button" aria-label="返回任务详情" onClick={close}><IconLeft /></button>
      <h2>{action.label}</h2>
    </header>
    <div className="mobile-action-page__body">
      {action.code === 'TRANSFER' ? <section className="mobile-action-page__section">
        <div className="mobile-action-page__section-head"><strong>转交人</strong><button type="button" disabled={editingLocked} onClick={() => { if (interactionRef.current) return; setDraftUsers(selectedUsers); setKeyword(''); props.onSearchTargets(''); setUserSheetOpen(true); }}>＋ 添加</button></div>
        {selectedUsers.length ? <div className="mobile-action-page__selected">{selectedUsers.map((id) => <span key={id}>{candidates.find(candidate => candidate.userId === id)?.name ?? id}</span>)}</div> : <p className="mobile-action-page__hint">请选择一位转交人</p>}
      </section> : null}
      <section className="mobile-action-page__section">
        <label className="mobile-action-page__label" htmlFor="mobile-approval-opinion">审批意见{action.requiresOpinion ? <span>必填</span> : null}</label>
        <textarea id="mobile-approval-opinion" value={opinion} disabled={editingLocked} maxLength={4000} onChange={(event) => { if (!interactionRef.current && !editingLocked) props.onOpinionChange(event.target.value); }} placeholder="请输入审批意见" rows={6} />
        <MobileComposerAttachments attachments={props.attachments} disabled={editingLocked} uploading={props.uploading} onAddFiles={addFiles} onRemove={id => { if (!interactionRef.current && !editingLocked) props.onRemoveAttachment(id); }} />
      </section>
    </div>
    <footer className="mobile-action-page__footer"><button type="button" disabled={closing || props.busy || props.uploading || userSheetOpen || action.requiresOpinion && !opinion.trim() || action.code === 'TRANSFER' && !props.targetUserId} onClick={submit}>确认{action.label}</button></footer>
    </div>

    {userSheetOpen ? <div className="mobile-user-picker" role="dialog" aria-modal="true" aria-label="选择转交人">
      <button className="mobile-user-picker__mask" type="button" aria-label="关闭选人面板" onClick={() => setUserSheetOpen(false)} />
      <div className="mobile-user-picker__sheet">
        <div className="mobile-user-picker__head"><strong>选择转交人</strong><button type="button" onClick={() => setUserSheetOpen(false)}>取消</button></div>
        <label className="mobile-user-picker__search"><IconSearch /><input type="search" value={keyword} disabled={editingLocked} onChange={(event) => { if (interactionRef.current || editingLocked) return; setKeyword(event.target.value); props.onSearchTargets(event.target.value); }} placeholder="搜索用户姓名或部门" /></label>
        <div className="mobile-user-picker__list">
          {visibleCandidates.map((candidate) => <button className="mobile-user-picker__user" type="button" disabled={editingLocked} key={candidate.userId} aria-label={`选择${candidate.name}`} aria-pressed={draftUsers.includes(candidate.userId)} onClick={() => { if (!interactionRef.current && !editingLocked) setDraftUsers([candidate.userId]); }}>
            <span className="mobile-user-picker__avatar">{candidate.name.slice(0, 1)}</span>
            <span className="mobile-user-picker__person"><strong>{candidate.name}</strong><small>{candidate.organizationName}</small></span>
            <span className={`mobile-user-picker__check${draftUsers.includes(candidate.userId) ? ' is-selected' : ''}`}>{draftUsers.includes(candidate.userId) ? '✓' : ''}</span>
          </button>)}
          {!visibleCandidates.length ? <div className="mobile-user-picker__empty">没有匹配的用户</div> : null}
        </div>
        <div className="mobile-user-picker__footer"><button type="button" disabled={editingLocked} onClick={() => { if (interactionRef.current || editingLocked) return; if (draftUsers.length !== 1) { mobileMessage.warning('请选择一位转交人'); return; } props.onTargetChange(draftUsers[0]); setUserSheetOpen(false); }}>确认（{draftUsers.length}）</button></div>
      </div>
    </div> : null}
  </div>;
}

export default MobileTaskActionPage;
